import { findParentNode } from '@tiptap/core';
import type { SetImageOptions } from '@tiptap/extension-image';
import { GapCursor } from '@tiptap/pm/gapcursor';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Selection, Transaction } from '@tiptap/pm/state';
import { CellSelection } from '@tiptap/pm/tables';
import type { ChainedCommands, Editor } from '@tiptap/react';
import isNil from 'lodash/isNil';

import { PARAGRAPH_NODE_NAME } from '@ui/components/editor/richTextUtils';
import { isTemplateImageSource } from '@ui/components/editor/templateLinkUtils';
import {
  readTemplateTableAlignment,
  TABLE_NODE_NAME,
  TEMPLATE_TEXT_ALIGNMENTS,
  type TemplateTableSize,
  type TemplateTextAlignment,
  toTemplateTableAlignment,
} from '@ui/components/editor/templateTextUtils';

const PREVENT_UPDATE_META = 'preventUpdate';
const ADD_TO_HISTORY_META = 'addToHistory';
const DEFAULT_TEXT_ALIGNMENT: TemplateTextAlignment = 'left';

interface SelectedTable {
  pos: number;
  node: ProseMirrorNode;
}

export const redrawEditorSilently = (editor: Editor): void => {
  editor.view.dispatch(editor.state.tr.setMeta(PREVENT_UPDATE_META, true).setMeta(ADD_TO_HISTORY_META, false));
};

export const insertTemplateImage = (chain: ChainedCommands, image: SetImageOptions): boolean => {
  const src = image.src.trim();
  if (!isTemplateImageSource(src)) {
    return false;
  }
  return chain.setImage({ ...image, src }).run();
};

export const insertTemplateToken = (editor: Editor, token: string, keepsSelection: boolean): boolean => {
  if (!editor.isEditable) {
    return false;
  }
  return editor
    .chain()
    .focus(keepsSelection ? null : 'end')
    .insertTemplateTag(token)
    .run();
};

const readInheritedTextAlignment = (selection: Selection): TemplateTextAlignment | null => {
  const alignment = TEMPLATE_TEXT_ALIGNMENTS.find(
    (textAlignment) => textAlignment === selection.$from.parent.attrs.textAlign
  );
  return alignment === DEFAULT_TEXT_ALIGNMENT ? null : (alignment ?? null);
};

const isTableNode = (node: ProseMirrorNode): boolean => node.type.name === TABLE_NODE_NAME;

const isInsertedBy = (transaction: Transaction, position: number): boolean =>
  transaction.mapping.invert().mapResult(position).deleted;

const alignInsertedTable = (transaction: Transaction, alignment: TemplateTextAlignment | null): void => {
  const table = findParentNode(isTableNode)(transaction.selection);
  if (isNil(table) || isNil(alignment)) {
    return;
  }
  const tableAlignment = toTemplateTableAlignment(alignment);
  if (!isNil(tableAlignment)) {
    transaction.setNodeMarkup(table.pos, undefined, { ...table.node.attrs, align: tableAlignment });
  }
  const tableEnd = table.pos + table.node.nodeSize;
  if (tableEnd !== transaction.doc.content.size) {
    return;
  }
  transaction.insert(tableEnd, transaction.doc.type.schema.nodes[PARAGRAPH_NODE_NAME].create({ textAlign: alignment }));
};

const alignParagraphCreatedAfterRule = (transaction: Transaction, alignment: TemplateTextAlignment | null): void => {
  const { $from } = transaction.selection;
  if (isNil(alignment) || $from.parent.type.name !== PARAGRAPH_NODE_NAME || !isInsertedBy(transaction, $from.pos)) {
    return;
  }
  transaction.setNodeMarkup($from.before(), undefined, { ...$from.parent.attrs, textAlign: alignment });
};

export const insertTemplateTable = (editor: Editor, size: TemplateTableSize): boolean => {
  const alignment = readInheritedTextAlignment(editor.state.selection);
  return editor
    .chain()
    .focus()
    .insertTable({ ...size, withHeaderRow: false })
    .command(({ tr }) => {
      alignInsertedTable(tr, alignment);
      return true;
    })
    .run();
};

export const insertTemplateHorizontalRule = (editor: Editor): boolean => {
  const alignment = readInheritedTextAlignment(editor.state.selection);
  return editor
    .chain()
    .focus()
    .setHorizontalRule()
    .command(({ tr }) => {
      alignParagraphCreatedAfterRule(tr, alignment);
      return true;
    })
    .run();
};

const findTableNextToGapCursor = ({ $from }: GapCursor): SelectedTable | null => {
  const { nodeAfter, nodeBefore } = $from;
  if (!isNil(nodeAfter) && isTableNode(nodeAfter)) {
    return { pos: $from.pos, node: nodeAfter };
  }
  if (!isNil(nodeBefore) && isTableNode(nodeBefore)) {
    return { pos: $from.pos - nodeBefore.nodeSize, node: nodeBefore };
  }
  return null;
};

const findTableLevelSelection = (selection: Selection): SelectedTable | null => {
  if (selection instanceof CellSelection) {
    const spansEveryCell = selection.isRowSelection() && selection.isColSelection();
    return spansEveryCell ? (findParentNode(isTableNode)(selection) ?? null) : null;
  }
  if (selection instanceof GapCursor) {
    return findTableNextToGapCursor(selection);
  }
  return null;
};

export const isTemplateAlignmentActive = (editor: Editor, alignment: TemplateTextAlignment): boolean => {
  const table = findTableLevelSelection(editor.state.selection);
  if (isNil(table)) {
    return editor.isActive({ textAlign: alignment });
  }
  return (readTemplateTableAlignment(table.node) ?? DEFAULT_TEXT_ALIGNMENT) === alignment;
};

export const toggleTemplateAlignment = (editor: Editor, alignment: TemplateTextAlignment): boolean => {
  const table = findTableLevelSelection(editor.state.selection);
  if (isNil(table)) {
    return editor.chain().focus().toggleTextAlign(alignment).run();
  }
  const currentAlignment = readTemplateTableAlignment(table.node);
  const requestedAlignment = toTemplateTableAlignment(alignment);
  const nextAlignment = currentAlignment === requestedAlignment ? null : requestedAlignment;
  if (nextAlignment === currentAlignment) {
    return editor.commands.focus();
  }
  return editor
    .chain()
    .focus()
    .command(({ tr }) => {
      tr.setNodeMarkup(table.pos, undefined, { ...table.node.attrs, align: nextAlignment });
      return true;
    })
    .run();
};
